import { History } from 'history';
import useComponentMount from '../hooks/useComponentMount';
import { USERS_PER_PAGE } from '../../utils/constants';
import usePagination from '../../components/Pagination/hooks/usePagination';
import {
  AllAdminsResponse,
  GET_ALL_ADMINS,
} from '../../apollo/queries/adminUsers/getAdmins.queries';
import { AdminRole } from '../AddAdminUser/hooks/useAddAdminUser';
import appLocalStorage from '../../utils/AppLocalStorage';

interface LocationState {
  pageNumber: number;
}

interface Props {
  history: History;
}

const useAdminsPage = ({ history }: Props) => {
  const pageNumber = history.location.state
    ? (history.location.state as LocationState).pageNumber
    : 1;

  const {
    queryData: { loading, error, data, refetch },
    pageData: {
      currentPage,
      search,
      totalPages,
      totalCount,
      pageSize,
      pageStats,
      tenantCode,
    },
    actions: {
      setCurrentPage,
      setSearch,
      setPageSize,
      onSearch,
      setTenant,
      onTenant,
    },
  } = usePagination<AllAdminsResponse>({
    query: GET_ALL_ADMINS,
    variables: {
      roles: [
        AdminRole.SUPER_ADMIN_ORG,
        AdminRole.EMARKING_ADMIN,
        AdminRole.ALEF_CONSOLE_SUPPORT,
      ],
    },
    responseField: 'searchUsers',
    pageSize: USERS_PER_PAGE,
    currentPage: pageNumber,
    isTenantSpecific: true,
  });

  const isComponentMounted = useComponentMount();

  const resetPagination = () => {
    setCurrentPage(1);
  };

  const onSearchChange = (value: string) => {
    resetPagination();
    onSearch(value);
  };

  const onTenantChange = (tenant: string) => {
    resetPagination();
    onTenant(tenant);
  };

  const onPageSizeChange = (size: number) => {
    setPageSize(size);
  };

  const retrieveData = (data: AllAdminsResponse) => {
    if (data?.searchUsers) {
      return data.searchUsers.users ? data.searchUsers.users : data.searchUsers;
    }
    return [];
  };

  return {
    search,
    isViewOnlyRole: appLocalStorage.isViewOnlyRole(),
    tenantCode,
    onSearchChange,
    onTenantChange,
    setCurrentPage,
    setSearch,
    setTenant,
    adminsData: {
      currentPage,
      loading,
      error,
      admins: retrieveData(data),
      pageNumber,
      totalPages,
      totalCount,
      pageSize,
    },
    onPageSizeChange,
    refetch,
    pageStats,
    isComponentMounted,
  };
};

export default useAdminsPage;
